import type { StatusMap } from "@vixio/ui";

export const INTERVALS = { monthly: "Mensal", quarterly: "Trimestral", yearly: "Anual" } as const;
export type Interval = keyof typeof INTERVALS;
export const PROVIDERS = { asaas: "Asaas", efi: "Efí", inter: "Inter", bb: "Banco do Brasil", itau: "Itaú", cnab: "CNAB 240", manual: "Manual" } as const;
export type Provider = keyof typeof PROVIDERS;
export const CHARGE_KINDS = { boleto: "Boleto", pix: "PIX", card: "Cartão" } as const;
export type ChargeKind = keyof typeof CHARGE_KINDS;

export const SUB_STATUS: StatusMap = {
  active: { label: "ativa", tone: "ok" },
  paused: { label: "pausada", tone: "warn" },
  canceled: { label: "cancelada", tone: "neutral" },
  ended: { label: "encerrada", tone: "neutral" },
};
export const CHARGE_STATUS: StatusMap = {
  pending: { label: "na fila", tone: "info" },
  processing: { label: "registrando", tone: "info" },
  registered: { label: "registrada", tone: "accent" },
  paid: { label: "paga", tone: "ok" },
  refunded: { label: "estornada", tone: "warn" },
  canceled: { label: "cancelada", tone: "neutral" },
  failed: { label: "falhou", tone: "crit" },
};

export interface Plan {
  id: string;
  name: string;
  amount_cents: number;
  billing_interval: Interval;
  offer_slug: string | null;
  description: string | null;
  active: boolean;
}

export interface Subscription {
  id: string;
  issuer_id: string | null;
  person_id: string | null;
  counterparty_name: string;
  counterparty_doc: string | null;
  plan_id: string | null;
  amount_cents: number;
  billing_interval: Interval;
  offer_slug: string | null;
  description: string;
  starts_on: string;
  ends_on: string | null;
  next_billing_on: string;
  due_days: number;
  charge_provider: Provider | null;
  charge_kind: ChargeKind | null;
  status: "active" | "paused" | "canceled" | "ended";
  status_reason: string | null;
  origin_type: string | null;
  origin_id: string | null;
  created_at: string;
}

export interface Charge {
  id: string;
  installment_id: string;
  receivable_id: string;
  subscription_id: string | null;
  provider: Provider;
  kind: ChargeKind;
  status: string;
  amount_cents: number;
  due_date: string;
  description: string | null;
  customer: { name?: string; doc?: string; email?: string } | null;
  boleto_line: string | null;
  pix_copy_paste: string | null;
  pdf_url: string | null;
  paid_at: string | null;
  attempts: number;
  last_error: string | null;
  created_at: string;
}

export const CANCEL_STATUS: StatusMap = {
  pendente: { label: "na fila", tone: "info" },
  processando: { label: "baixando", tone: "info" },
  cancelada: { label: "baixada", tone: "ok" },
  nao_necessaria: { label: "sem registro", tone: "neutral" },
  falhou: { label: "falhou", tone: "crit" },
};

export interface ProviderCancellation {
  id: string;
  charge_id: string;
  provider: Provider;
  provider_charge_id: string | null;
  reason: string | null;
  status: "pendente" | "processando" | "cancelada" | "nao_necessaria" | "falhou";
  attempts: number;
  max_attempts: number;
  next_attempt_at: string;
  outcome: string | null;
  last_error: string | null;
  done_at: string | null;
  description: string;
  amount_cents: number;
  due_date: string;
  customer_name: string | null;
  created_at: string;
}

export const REMINDER_STATUS: StatusMap = {
  agendado: { label: "agendado", tone: "info" },
  enviado: { label: "aviso pedido", tone: "ok" },
  pulado: { label: "pulado", tone: "neutral" },
  cancelado: { label: "cancelado", tone: "neutral" },
};

export interface ReminderSettings {
  active: boolean;
  days_before: number[];
  on_due_day: boolean;
  days_after: number[];
  channels: Array<"email" | "whatsapp">;
  only_with_charge: boolean;
  updated_at?: string;
}

export interface DueReminder {
  id: string;
  installment_id: string;
  due_date: string;
  offset_days: number;
  send_on: string;
  source: string;
  status: "agendado" | "enviado" | "pulado" | "cancelado";
  reason: string | null;
  channels: string[];
  sent_at: string | null;
  counterparty_name: string | null;
  description: string | null;
  open_cents: number | null;
}

/** "3 dias antes", "no dia", "2 dias depois". */
export function offsetLabel(offset: number): string {
  if (offset === 0) return "no dia";
  const n = Math.abs(offset);
  return `${n} ${n === 1 ? "dia" : "dias"} ${offset < 0 ? "antes" : "depois"}`;
}

/** "3, 1" → [3, 1]: inteiros de 1 a 30, sem repetição. */
export function parseDays(text: string): number[] | null {
  const parts = text.split(/[,;\s]+/).map((t) => t.trim()).filter(Boolean);
  const out: number[] = [];
  for (const p of parts) {
    if (!/^\d{1,2}$/.test(p)) return null;
    const n = Number(p);
    if (n < 1 || n > 30) return null;
    if (!out.includes(n)) out.push(n);
  }
  return out.length > 5 ? null : out;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "billing.upsert_plan": { args: { p_org: U; p_name: string; p_amount_cents: number; p_interval?: Interval; p_offer_slug?: string | null; p_description?: string | null; p_active?: boolean; p_id?: U | null }; returns: U };
    "billing.create_subscription": {
      args: {
        p_org: U; p_counterparty_name: string; p_description: string; p_starts_on: string; p_plan?: U | null; p_amount_cents?: number | null;
        p_interval?: Interval | null; p_issuer?: U | null; p_person?: U | null; p_counterparty_doc?: string | null; p_offer_slug?: string | null;
        p_term_months?: number | null; p_due_days?: number; p_charge_provider?: Provider | null; p_charge_kind?: ChargeKind | null;
      };
      returns: U;
    };
    "billing.set_subscription_status": { args: { p_subscription: U; p_status: "active" | "paused" | "canceled"; p_reason?: string | null }; returns: null };
    "billing.run_cycle": { args: { p_date?: string | null; p_org?: U | null }; returns: { date: string; billed: number; ended: number; errors: unknown[] } };
    "billing.request_charge": { args: { p_installment: U; p_provider: Provider; p_kind: ChargeKind }; returns: U };
    "billing.cancel_charge": { args: { p_charge: U; p_reason: string }; returns: null };
    "billing.retry_provider_cancellation": { args: { p_id: U }; returns: null };
    "billing.set_reminder_settings": {
      args: { p_org: U; p_active: boolean; p_days_before: number[]; p_on_due_day: boolean; p_days_after: number[]; p_channels: string[]; p_only_with_charge: boolean };
      returns: ReminderSettings & { canceled: number };
    };
    "billing.send_due_reminders": { args: { p_date?: string | null; p_org?: U | null }; returns: { date: string; scheduled: number; sent: number; skipped: number; canceled: number } };
  }
}
